/* CamGuard interaction analytics. Shopify owns transport and pixel consent.
 * No form values, customer data, URLs, storage, cohort assignment or native ecommerce events.
 * Event contract and pixel companion: qa/analytics/CONTRACT.md in the release evidence.
 */
(() => {
  'use strict';
  if (window.CgauCommerceEvents) return;

  const fields = Object.freeze({
    setup_viewed: [], setup_changed: ['model', 'quantity'],
    setup_added: ['model', 'quantity', 'value', 'currency'],
    setup_error: ['stage'], setup_stock_changed: [], recording_demo_used: ['action'],
    finder_completed: ['result_count'], finder_no_results: ['result_count'],
    compare_opened: ['quantity'], compare_product_clicked: ['product_id'],
    support_routed: ['route'], support_enquiry_started: ['route'],
    experiment_exposed: [], experiment_clicked: []
  });
  const routes = ['choosing', 'compatibility', 'setup', 'delivery', 'problem'];
  const sourceFor = name => name.startsWith('setup_') ? 'setup' : name.startsWith('recording_') ? 'recording' : name.split('_')[0];
  const integer = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;

  function sanitise(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input) || !Object.hasOwn(fields, input.name)) return null;
    const result = {schema_version: 1, name: input.name, source: sourceFor(input.name)};
    for (const field of fields[input.name]) {
      const value = input[field];
      if (field === 'model' && ['T8134', 'T8160', 'T8030'].includes(value)) result.model = value;
      if (field === 'quantity' && integer(value, input.name.startsWith('setup_') ? 1 : 0, 4)) result.quantity = value;
      if (field === 'result_count' && integer(value, 0, 1000)) result.result_count = value;
      if (field === 'product_id' && typeof value === 'string' && /^[1-9]\d{0,19}$/.test(value)) result.product_id = value;
      if (field === 'currency' && typeof value === 'string' && /^[A-Z]{3}$/.test(value)) result.currency = value;
      if (field === 'value' && typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1000000) result.value = Math.round(value * 100) / 100;
      if (field === 'action' && ['play', 'scrub', 'pause'].includes(value)) result.action = value;
      if (field === 'stage' && ['cart', 'stock', 'catalogue'].includes(value)) result.stage = value;
      if (field === 'route' && routes.includes(value)) result.route = value;
    }
    if (!result.currency) delete result.value;
    if (input.experiment === 'setup_entry_v1' && ['control', 'cost'].includes(input.variant) && (input.name.startsWith('setup_') || input.name.startsWith('experiment_'))) {
      result.experiment = input.experiment; result.variant = input.variant;
    }
    if (input.name.startsWith('experiment_') && !result.experiment) return null;
    if (input.name.startsWith('support_') && !result.route) return null;
    if (input.name === 'compare_product_clicked' && !result.product_id) return null;
    if (input.name === 'recording_demo_used' && !result.action) return null;
    if (input.name === 'finder_no_results') result.result_count = 0;
    return result;
  }

  let destroyed = false, privacyRequested = false;
  let deniedByEvent = false;
  const recent = new Map(), rate = [];
  const counts = {published: 0, denied: 0, invalid: 0, duplicate: 0, limited: 0, qa: 0, unavailable: 0, failed: 0};
  function allowed() {
    if (destroyed || deniedByEvent) return false;
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed?.() === true && privacy?.marketingAllowed?.() === true;
    } catch (_) { return false; }
  }
  function loadPrivacy() {
    const shopify = window.Shopify;
    if (destroyed || privacyRequested || (typeof shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function' && typeof shopify?.customerPrivacy?.marketingAllowed === 'function') || typeof shopify?.loadFeatures !== 'function') return;
    privacyRequested = true;
    try { shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.1'}], () => {}); }
    catch (_) { /* Fail closed; there is no event queue or retry loop. */ }
  }
  function consentChanged(event) {
    const detail = event.detail;
    // Explicit denial wins immediately, including the interval before API state catches up.
    if (detail?.analyticsAllowed === false || detail?.marketingAllowed === false) deniedByEvent = true;
    else if (detail?.analyticsAllowed === true && detail?.marketingAllowed === true) deniedByEvent = false;
    if (!allowed()) { recent.clear(); rate.length = 0; }
  }
  function publish(input) {
    loadPrivacy();
    if (!allowed()) { counts.denied++; return false; }
    let data;
    try { data = sanitise(input); } catch (_) { data = null; }
    if (!data) { counts.invalid++; return false; }
    const qa = input.qa === true || input.debug_mode === true || window.Shopify?.designMode === true || ['unpublished', 'development'].includes(window.Shopify?.theme?.role) || document.documentElement.dataset.cgauQa === 'true' || ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);
    if (qa && input.debug_mode !== true) { counts.qa++; return false; }
    if (qa) { data.qa = true; data.debug_mode = true; }
    const analytics = window.Shopify?.analytics;
    if (typeof analytics?.publish !== 'function') { counts.unavailable++; return false; }
    const now = Date.now(), fingerprint = JSON.stringify(data);
    for (const [key, expiry] of recent) if (expiry <= now) recent.delete(key);
    if (recent.has(fingerprint)) { counts.duplicate++; return false; }
    while (rate.length && rate[0] <= now - 60000) rate.shift();
    if (rate.length >= 60) { counts.limited++; return false; }
    recent.set(fingerprint, now + (data.name === 'recording_demo_used' && data.action === 'scrub' ? 2000 : 1000));
    rate.push(now);
    try {
      const result = analytics.publish('cgau_commerce', data);
      counts.published++;
      if (result && typeof result.catch === 'function') result.catch(() => { counts.failed++; });
      return true;
    } catch (_) { counts.failed++; return false; }
  }
  const onCommerce = event => publish(event.detail);
  function onClick(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('a[data-help-route], a[data-help-enquiry]') : null;
    if (!link || !link.closest('[data-cgau-help]')) return;
    const route = link.dataset.helpRoute, enquiry = link.dataset.helpEnquiry;
    if (routes.includes(route)) publish({name: 'support_routed', route});
    // The help module prevents default only after successfully creating an editable draft.
    else if (routes.includes(enquiry) && event.defaultPrevented) publish({name: 'support_enquiry_started', route: enquiry});
  }
  const destroy = () => {
    destroyed = true; recent.clear(); rate.length = 0;
    document.removeEventListener('cgau:commerce', onCommerce);
    document.removeEventListener('click', onClick);
    document.removeEventListener('visitorConsentCollected', consentChanged);
    document.removeEventListener('DOMContentLoaded', loadPrivacy);
    delete window.CgauCommerceEvents;
  };
  window.CgauCommerceEvents = Object.freeze({version: 1, allowed, destroy, status: () => ({...counts, allowed: allowed()})});
  document.addEventListener('cgau:commerce', onCommerce);
  document.addEventListener('click', onClick);
  document.addEventListener('visitorConsentCollected', consentChanged);
  document.addEventListener('DOMContentLoaded', loadPrivacy, {once: true});
  loadPrivacy();
})();
